import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lock, Mail, MailOpen, Trash2, RefreshCw, LogOut, Phone, Inbox, ArrowLeft, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { API } from "../mock/mock";

const Admin = () => {
  const { toast } = useToast();
  const [key, setKey] = useState(sessionStorage.getItem("meilleur_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (adminKey) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiries`, { headers: { "X-Admin-Key": adminKey } });
      if (res.status === 401) throw new Error("unauthorized");
      const data = await res.json();
      setEnquiries(data);
      setAuthed(true);
    } catch {
      setAuthed(false);
      sessionStorage.removeItem("meilleur_admin_key");
      toast({ title: "Access denied", description: "Invalid admin passcode." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const submit = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API}/admin/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: input }),
    });
    const data = await res.json();
    if (data.valid) {
      sessionStorage.setItem("meilleur_admin_key", input);
      setKey(input);
    } else {
      toast({ title: "Access denied", description: "Invalid admin passcode." });
    }
  };

  const logout = () => {
    sessionStorage.removeItem("meilleur_admin_key");
    setKey("");
    setAuthed(false);
    setInput("");
  };

  const toggleRead = async (en) => {
    const res = await fetch(`${API}/enquiries/${en.id}/read`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Admin-Key": key },
      body: JSON.stringify({ read: !en.read }),
    });
    if (res.ok) setEnquiries((list) => list.map((x) => (x.id === en.id ? { ...x, read: !en.read } : x)));
  };

  const remove = async (id) => {
    const res = await fetch(`${API}/enquiries/${id}`, { method: "DELETE", headers: { "X-Admin-Key": key } });
    if (res.ok) setEnquiries((list) => list.filter((x) => x.id !== id));
  };

  const unread = enquiries.filter((e) => !e.read).length;

  const exportCsv = () => {
    if (enquiries.length === 0) return;
    const headers = ["First Name", "Last Name", "Email", "Phone", "Message", "Source", "Read", "Received"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = enquiries.map((e) =>
      [e.firstName, e.lastName, e.email, e.phone, e.message, e.source, e.read ? "Yes" : "No", new Date(e.createdAt).toLocaleString()].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `meilleur-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Export ready", description: `${enquiries.length} enquiries downloaded as CSV.` });
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-noir flex items-center justify-center px-6">
        <form onSubmit={submit} className="lux-card w-full max-w-md p-10 md:p-12">
          <div className="w-14 h-14 border border-[var(--line)] flex items-center justify-center mb-8">
            <Lock size={22} className="text-[var(--gold)]" />
          </div>
          <div className="eyebrow mb-3">Admin Access</div>
          <h1 className="font-serif-display text-4xl mb-8">Enquiry <span className="gold-text italic">inbox</span></h1>
          <Label className="eyebrow text-[0.6rem]">Passcode</Label>
          <Input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter admin passcode"
            className="mt-3 mb-7 bg-[var(--panel-2)] border-[var(--line)] h-12 focus:border-[var(--gold)] text-[var(--ivory)]"
          />
          <button type="submit" className="btn-gold w-full justify-center">Enter Inbox</button>
          <Link to="/" className="mt-6 flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-[var(--ivory-muted)] hover:text-[var(--gold-light)] transition-colors">
            <ArrowLeft size={13} /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-noir py-10 px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Meilleur · Admin</div>
            <h1 className="font-serif-display text-4xl md:text-5xl flex items-center gap-4">
              Enquiry Inbox
              {unread > 0 && <span className="text-sm font-body bg-[var(--green)] text-[var(--cream)] px-3 py-1 rounded-full">{unread} new</span>}
            </h1>
          </div>
          <div className="flex gap-3">
            <button onClick={exportCsv} className="btn-ghost" disabled={enquiries.length === 0}>
              <Download size={14} /> Export CSV
            </button>
            <button onClick={() => load(key)} className="btn-ghost" disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost"><LogOut size={14} /> Logout</button>
          </div>
        </div>

        {enquiries.length === 0 ? (
          <div className="lux-card p-20 text-center">
            <Inbox size={40} className="text-[var(--gold)] mx-auto mb-5 opacity-60" />
            <p className="text-[var(--ivory-muted)] font-body">No enquiries yet. New submissions from the contact page will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((en) => (
              <div key={en.id} className={`lux-card p-6 md:p-7 ${en.read ? "opacity-70" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif-display text-2xl">{en.firstName} {en.lastName}</h3>
                      {!en.read && <span className="text-[0.6rem] tracking-[0.16em] uppercase bg-[rgba(20,81,60,0.1)] text-[var(--green)] border border-[var(--line)] px-2.5 py-1">New</span>}
                      <span className="text-[0.62rem] tracking-[0.12em] uppercase text-[var(--ivory-muted)] border border-[var(--line)] px-2.5 py-1">{en.source}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-[var(--ivory-muted)] font-body">
                      <a href={`mailto:${en.email}`} className="flex items-center gap-2 hover:text-[var(--gold-light)]"><Mail size={14} className="text-[var(--gold)]" /> {en.email}</a>
                      {en.phone && <a href={`tel:${en.phone}`} className="flex items-center gap-2 hover:text-[var(--gold-light)]"><Phone size={14} className="text-[var(--gold)]" /> {en.phone}</a>}
                    </div>
                    {en.message && <p className="mt-4 text-[var(--ivory)] font-body leading-relaxed border-l border-[var(--line)] pl-4">{en.message}</p>}
                    <p className="mt-4 text-xs text-[var(--ivory-muted)] font-body">{new Date(en.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => toggleRead(en)} title={en.read ? "Mark unread" : "Mark read"} className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--gold)] hover:border-[var(--gold)] transition-colors">
                      {en.read ? <MailOpen size={16} /> : <Mail size={16} />}
                    </button>
                    <button onClick={() => remove(en.id)} title="Delete" className="w-10 h-10 border border-[var(--line)] flex items-center justify-center text-[var(--ivory-muted)] hover:text-red-400 hover:border-red-400/50 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
